import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ICompactDataGridAttrInfo } from "../../CompactDatagrid";
import { IDataGrid } from "../../IDataGrid";
import { Column } from "../Columns/Column";
import { CompactTableCell } from "./CompactTableCell";
import { DataRow } from "./DataRow";
import { ICompactDataRowElement } from "./IDataRow";

export class CompactDataRow extends DataRow implements ICompactDataRowElement {
    public visibleAttrs: ICompactDataGridAttrInfo;

    protected _compactTLeft: CompactTableCell[];
    protected _compactTRight: CompactTableCell[];
    protected _compactBottom: CompactTableCell[];

    private _rowControlElement: HTMLElement;

    public override init(dataRow: IRowData, ownerGrid: IDataGrid<CompactDataRow>, rowControlElement: HTMLElement): void {
        if (this.checkAndLogDisposedUsage("init")) return;

        this._init(dataRow, ownerGrid, rowControlElement);
        this._initCells(dataRow);
        this._registerRowClickEvent();
        //this.dataRow = row;
    }

    /**
     * Základní inicializace vlastností řádku
     * @param dataRow řádek
     * @param ownerGrid rodičovský DataGrid
     * @param rowControlElement element s ovládacími prvky, který se vloží do nulté buňky řádku
     */
    protected override _init(dataRow: IRowData, ownerGrid: IDataGrid<CompactDataRow>, rowControlElement: HTMLElement): void {
        if (this.checkAndLogDisposedUsage("_init")) return;

        this._dataGrid = ownerGrid;
        this._dataCells = new Array<CompactTableCell>();
        this._compactTLeft = [];
        this._compactTRight = [];
        this._compactBottom = [];

        if (rowControlElement) {
            this._rowControlElement = rowControlElement;
        }

        this.dataKey = dataRow[this._dataGrid.dataSource.dataKey].valueOf();

        if (!this.dataKey)
            throw Error("DataRow has invalid dataKey");
    }

    /**
     * Inicializace datových buněk řádku
     * @param dataRow řádek
     * @param columns sloupce k inicializaci... pokud je null, tak se použijí sloupce z dataGridu
     */
    protected override _initCells(dataRow: IRowData, _frozenCount?: number, columns?: Column[]): void {
        if (this.checkAndLogDisposedUsage("_initCells")) return;

        if (columns === null || columns === undefined) columns = this._dataGrid.columns; // columns ??= this._dataGrid.columns
        const onlyBasicAttrs = this.visibleAttrs == undefined;
        const dataRowCaseInsensitive = ObjectUtils.getPropertiesCaseInsensitive<IAttrValue>(dataRow ?? {});

        for (let i: number = 0; i < columns.length; i++) {
            if (onlyBasicAttrs && i >= 3)
                break;

            const column: Column = columns[i];

            // Kvuli LOVAttribute musi byt Object data jako pole, protoze obsahuje vic zobrazovatelnych atributu
            const data: Array<IAttrValue> = [];
            for (const d of column.attrNames) {
                data.push(dataRowCaseInsensitive.get(d));
            }

            const cell: CompactTableCell = new CompactTableCell(this._dataGrid, this, i, column, data);
            //this.DataCells.push(cell);

            // pokud pro kompaktni zobrazeni nebyly vyplneny attributy, pouziji se prvni 3
            if (onlyBasicAttrs) {
                switch (i) {
                    case 0:
                        this._compactTLeft.push(cell);
                        break;
                    case 1:
                        this._compactTRight.push(cell);
                        break;
                    case 2:
                        this._compactBottom.push(cell);
                        break;
                }
                this._dataCells.push(cell);
            } else {
                for (let j = 0; j < this.visibleAttrs.topLeft.length; j++) {
                    if (this.visibleAttrs.topLeft[j] == column.attrName) {
                        this._dataCells.push(cell);
                        cell.order = j;
                        this._compactTLeft[j] = cell;
                    }
                }
                for (let j = 0; j < this.visibleAttrs.topRight.length; j++) {
                    if (this.visibleAttrs.topRight[j] == column.attrName) {
                        this._dataCells.push(cell);
                        cell.order = j;
                        this._compactTRight[j] = cell;
                    }
                }
                for (let j = 0; j < this.visibleAttrs.bottom.length; j++) {
                    if (this.visibleAttrs.bottom[j] == column.attrName) {
                        this._dataCells.push(cell);
                        cell.order = j;
                        this._compactBottom[j] = cell;
                    }
                }
            }
        }
    }

    public set isReadOnly(_value: boolean) {
        // Prazdny setter
    }

    public override select(): void {
        if (this.checkAndLogDisposedUsage("select")) return;

        const oldSel = this.isSelected;
        //const oldEdit = this.DataGrid.LastClickedRow.IsEdited;

        this._dataGrid.focus();
        this._dataGrid.isSelectedAll = false;
        this._dataGrid.clearSelectedRows();
        this.isSelected = true;
        this._dataGrid.lastClickedRow = this;

        if (!oldSel)
            this._dispatchSelectionChangeEvent(this.dataKey, { doubleClick: false, clickedRow: this });
    }

    /**
     * Renders the control to the specified HTML writer.
     */
    public override render(): HTMLElement {
        if (this.checkAndLogDisposedUsage("render")) return undefined;

        this.innerHTML = "";
        this._createCells();

        return this as HTMLElement;
    }

    protected _createCells(): void {
        if (this.checkAndLogDisposedUsage("_createCells")) return;

        const table = document.createElement("td");
        const top = document.createElement("div");
        const tleft = document.createElement("div");
        const tright = document.createElement("div");
        const bottomCells = document.createElement("div");
        const bottom = createElement({ classes: "bottom" });

        tleft.classList.add("left");
        tright.classList.add("right");
        top.classList.add("top-cells");
        bottomCells.classList.add("bottom-cells");

        top.appendChild(tleft);
        top.appendChild(tright);
        table.appendChild(top);
        bottom.appendChild(bottomCells);
        if (this._rowControlElement)
            bottom.appendChild(this._rowControlElement);
        table.appendChild(bottom);

        this.appendChild(table);

        const appendCell = (cell: CompactTableCell, el: HTMLDivElement) => {
            if (cell) {
                const newCell = cell.render();
                if (newCell)
                    el.appendChild(newCell);
            }
        };

        for (const cell of this._compactTRight) {
            appendCell(cell, tright);
        }
        for (const cell of this._compactBottom) {
            appendCell(cell, bottomCells);
        }
        for (const cell of this._compactTLeft) {
            appendCell(cell, tleft);
        }
    }

    public override setItemType(): void {
        // Prázdná metoda
    }
    //#endregion Methods

    //#region HTMLElement
    protected override _constructor(): void {
        super._constructor();

        this._compactTLeft = [];
        this._compactTRight = [];
        this._compactBottom = [];

        this.classList.add("CompactDataRow");
    }

    /**
     * Vyvola novou udalost, ktera se pouziva pri zmene vybranych radku
     * @param rowID -
     * @param data -
     */
    protected override _dispatchSelectionChangeEvent(rowID: string, data: any): void {
        if (this.checkAndLogDisposedUsage("_dispatchSelectionChangeEvent")) return;

        this.callEvent("SelectionChanged", { DataGrid: this._dataGrid, rowID: rowID, data: data }, true, true);
    }
    //#endregion HTMLElement
} //end DataRow

registerCustomElement("tsc-compactdatarow", CompactDataRow /*{ prototype: HeaderTemplate, extends: "htmltableheadercellelement" }*/);
